import { PropsWithChildren, useEffect, useState } from 'react'

import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import { FailedIcon, SubtractIcon } from '@arcade/web/icons/react'
import { NavigationDiscoverIcon } from '@arcade/web/icons/react'
import { Position, spacing } from '@cash-design-system/react'
import { Flex, Text } from '@cash-design-system/react'
import { useJourneyContext } from '@cash-web/shared-journey-data-access-context'

const CloseIcon = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  border-radius: 50%;
  left: ${({ theme }) => theme.spacing.m};
  top: ${({ theme }) => theme.spacing.m};
  padding: ${({ theme }) => theme.spacing.xs};
  background: ${({ theme }) => theme.colors.background};
  box-shadow: ${({ theme }) => theme.dropShadows.tile};
  border: none;
  cursor: pointer;
  z-index: 100;
  color: inherit;

  &:hover {
    background: ${({ theme }) => theme.colors.clickableContentBackgroundHover};
  }
`

const MinimizedButton = styled(CloseIcon)`
  left: 4rem;
  box-shadow: none;
`
const ZindexedPosition = styled(Position)`
  z-index: 100;
`

const ContentContainer = styled.div<{ isMinimized: boolean }>`
  transition: all 0.3s ease-in-out;
  transform-origin: bottom right;
  opacity: ${({ isMinimized }) => (isMinimized ? 0 : 1)};
  transform: ${({ isMinimized }) => (isMinimized ? 'scale(0.1) translateX(50px) translateY(50px)' : 'scale(1)')};
  pointer-events: ${({ isMinimized }) => (isMinimized ? 'none' : 'auto')};
  position: ${({ isMinimized }) => (isMinimized ? 'absolute' : 'relative')};
  bottom: ${({ isMinimized }) => (isMinimized ? '0' : 'auto')};
  right: ${({ isMinimized }) => (isMinimized ? '0' : 'auto')};
`

const MinimizedButtonContainer = styled.div<{ isMinimized: boolean }>`
  opacity: ${({ isMinimized }) => (isMinimized ? 1 : 0)};
  pointer-events: ${({ isMinimized }) => (isMinimized ? 'auto' : 'none')};
  position: ${({ isMinimized }) => (isMinimized ? 'relative' : 'absolute')};
  z-index: 101;
`

const Button = styled.button`
  appearance: none;
  cursor: pointer;
  padding: ${props => props.theme.spacing.default};
  padding-right: ${props => props.theme.spacing.l};
  background-color: ${props => props.theme.arcadeColors.semantic.backgroundApp};
  border: 1px solid ${props => props.theme.arcadeColors.semantic.borderSubtle};
  border-radius: ${props => props.theme.border.radius.pill};
  box-shadow: ${props => props.theme.dropShadows.topBar};
`

export const ChatEntryButton = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation()

  return (
    <Button onClick={onClick}>
      <Text typography="bodyDefault" color="textPlaceholder">
        <Flex gap="default">
          <NavigationDiscoverIcon size={24} />
          <span>{t('Resume chat', { context: 'moneybot composer CTA' })}</span>
        </Flex>
      </Text>
    </Button>
  )
}

export function useEscape(callback: () => void) {
  useEffect(() => {
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        callback()
      }
    }
    document.addEventListener('keydown', keydown)
    return () => document.removeEventListener('keydown', keydown)
  }, [callback])
}

export const JourneyMoneyBotModal = ({ onClose, children }: PropsWithChildren<{ onClose?: () => void }>) => {
  useEscape(() => {
    onClose?.()
  })

  const [isMinimized, setIsMinimized] = useState(false)
  const journey = useJourneyContext()

  // In the case of a new journey while minimized, we want to expand the modal
  useEffect(() => {
    setIsMinimized(false)
  }, [journey.active?.journeyId])

  const onMinimize = () => {
    setIsMinimized(!isMinimized)
  }

  return (
    <ZindexedPosition position="fixed" bottom={spacing.default} right={spacing.default}>
      <MinimizedButtonContainer isMinimized={isMinimized}>
        <ChatEntryButton
          onClick={() => {
            onMinimize?.()
          }}
        />
      </MinimizedButtonContainer>

      <ContentContainer isMinimized={isMinimized}>
        <CloseIcon
          onClick={() => {
            onClose?.()
          }}
          aria-label="Close chat"
        >
          <FailedIcon size={24} />
        </CloseIcon>
        <MinimizedButton
          onClick={() => {
            onMinimize?.()
          }}
          aria-label="Minimize chat"
        >
          <SubtractIcon variant="prominent" size={24} />
        </MinimizedButton>
        {children}
      </ContentContainer>
    </ZindexedPosition>
  )
}
